"use client";

import { FlexColumnAlignJustifyCenter } from "@/components/Flex";
import { ASSETS_URL } from "@/config/deployConstants";
import { RemoveAds } from "@/features/NavBar/components/NavbarSubscriptionBtns";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import FreestarAdSlot from "@freestar/pubfig-adslot-react-component";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { isEqual } from "lodash-es";
import React, { memo } from "react";
import { FREESTAR_PUBLISHER } from "../constants";
import { AdSlotId, Platform, ReplacementAds } from "../types";

const TURN_OFF_AD_BLOCK_URL = "https://help.knowt.com/en/articles/10298009-how-can-i-turn-off-my-ad-blocker";

/*
320x50
*/
const ad32050_ai_1 = `${ASSETS_URL}/images/radb/32050_ai_1.png`;
const ad32050_ai_2 = `${ASSETS_URL}/images/radb/32050_ai_2.png`;
const ad32050_radb_1 = `${ASSETS_URL}/images/radb/32050_radb_1.png`;
const ad32050_radb_2 = `${ASSETS_URL}/images/radb/32050_radb_2.png`;
const ad32050_radb_3 = `${ASSETS_URL}/images/radb/32050_radb_3.png`;
const ad32050_ap_1 = `${ASSETS_URL}/images/radb/32050_ap_1.png`;
const ad32050_ap_2 = `${ASSETS_URL}/images/radb/32050_ap_2.png`;
const ad32050_ap_3 = `${ASSETS_URL}/images/radb/32050_ap_3.png`;
const ad32050_ap_4 = `${ASSETS_URL}/images/radb/32050_ap_4.png`;
const ad32050_ap_5 = `${ASSETS_URL}/images/radb/32050_ap_5.png`;
const ad32050_ap_6 = `${ASSETS_URL}/images/radb/32050_ap_6.png`;

const fallbacks_320x50 = (adBlock = false) =>
    [
        {
            image: ad32050_ai_1,
            href: "/ai-pdf-summarizer",
            isAdBlock: false,
        },
        {
            image: ad32050_ai_2,
            href: "/ai-pdf-summarizer",
            isAdBlock: false,
        },
        {
            image: ad32050_radb_1,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad32050_radb_2,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad32050_radb_3,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad32050_ap_1,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad32050_ap_2,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad32050_ap_3,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad32050_ap_4,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad32050_ap_5,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad32050_ap_6,
            href: "/exams/AP",
            isAdBlock: false,
        },
    ].filter(({ isAdBlock }) => isAdBlock === false || adBlock === isAdBlock);

/*
320x100
*/
const ad320100_ai_1 = `${ASSETS_URL}/images/radb/320100_ai_1.png`;
const ad320100_ai_2 = `${ASSETS_URL}/images/radb/320100_ai_2.png`;
const ad320100_radb_1 = `${ASSETS_URL}/images/radb/320100_radb_1.png`;
const ad320100_radb_2 = `${ASSETS_URL}/images/radb/320100_radb_2.png`;
const ad320100_radb_3 = `${ASSETS_URL}/images/radb/320100_radb_3.png`;
const ad320100_ap_1 = `${ASSETS_URL}/images/radb/320100_ap_1.png`;
const ad320100_ap_2 = `${ASSETS_URL}/images/radb/320100_ap_2.png`;
const ad320100_ap_3 = `${ASSETS_URL}/images/radb/320100_ap_3.png`;
const ad320100_ap_4 = `${ASSETS_URL}/images/radb/320100_ap_4.png`;
const ad320100_ap_5 = `${ASSETS_URL}/images/radb/320100_ap_5.png`;
const ad320100_ap_6 = `${ASSETS_URL}/images/radb/320100_ap_6.png`;

const fallbacks_320x100 = (adBlock = false) =>
    [
        {
            image: ad320100_ai_1,
            href: "/ai-pdf-summarizer",
            isAdBlock: false,
        },
        {
            image: ad320100_ai_2,
            href: "/ai-pdf-summarizer",
            isAdBlock: false,
        },
        {
            image: ad320100_radb_1,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad320100_radb_2,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad320100_radb_3,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad320100_ap_1,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad320100_ap_2,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad320100_ap_3,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad320100_ap_4,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad320100_ap_5,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad320100_ap_6,
            href: "/exams/AP",
            isAdBlock: false,
        },
    ].filter(({ isAdBlock }) => isAdBlock === false || adBlock === isAdBlock);

/*
300x180
*/
const ad300180_ai_1 = `${ASSETS_URL}/images/radb/300180_ai_1.png`;
const ad300180_ai_2 = `${ASSETS_URL}/images/radb/300180_ai_2.png`;
const ad300180_radb_1 = `${ASSETS_URL}/images/radb/300180_radb_1.png`;
const ad300180_radb_2 = `${ASSETS_URL}/images/radb/300180_radb_2.png`;
const ad300180_radb_3 = `${ASSETS_URL}/images/radb/300180_radb_3.png`;
const ad300180_ap_1 = `${ASSETS_URL}/images/radb/300180_ap_1.png`;
const ad300180_ap_2 = `${ASSETS_URL}/images/radb/300180_ap_2.png`;
const ad300180_ap_3 = `${ASSETS_URL}/images/radb/300180_ap_3.png`;
const ad300180_ap_4 = `${ASSETS_URL}/images/radb/300180_ap_4.png`;
const ad300180_ap_5 = `${ASSETS_URL}/images/radb/300180_ap_5.png`;
const ad300180_ap_6 = `${ASSETS_URL}/images/radb/300180_ap_6.png`;

const fallbacks_300x180 = (adBlock = false) =>
    [
        {
            image: ad300180_ai_1,
            href: "/ai-pdf-summarizer",
            isAdBlock: false,
        },
        {
            image: ad300180_ai_2,
            href: "/ai-pdf-summarizer",
            isAdBlock: false,
        },
        {
            image: ad300180_radb_1,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad300180_radb_2,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad300180_radb_3,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad300180_ap_1,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad300180_ap_2,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad300180_ap_3,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad300180_ap_4,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad300180_ap_5,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad300180_ap_6,
            href: "/exams/AP",
            isAdBlock: false,
        },
    ].filter(({ isAdBlock }) => isAdBlock === false || adBlock === isAdBlock);

/*
300x250
*/
const ad300250_ai_1 = `${ASSETS_URL}/images/radb/300250_ai_1.png`;
const ad300250_ai_2 = `${ASSETS_URL}/images/radb/300250_ai_2.png`;
const ad300250_radb_1 = `${ASSETS_URL}/images/radb/300250_radb_1.png`;
const ad300250_radb_2 = `${ASSETS_URL}/images/radb/300250_radb_2.png`;
const ad300250_radb_3 = `${ASSETS_URL}/images/radb/300250_radb_3.png`;
const ad300250_ap_1 = `${ASSETS_URL}/images/radb/300250_ap_1.png`;
const ad300250_ap_2 = `${ASSETS_URL}/images/radb/300250_ap_2.png`;
const ad300250_ap_3 = `${ASSETS_URL}/images/radb/300250_ap_3.png`;
const ad300250_ap_4 = `${ASSETS_URL}/images/radb/300250_ap_4.png`;
const ad300250_ap_5 = `${ASSETS_URL}/images/radb/300250_ap_5.png`;
const ad300250_ap_6 = `${ASSETS_URL}/images/radb/300250_ap_6.png`;

const fallbacks_300x250 = (adBlock = false) =>
    [
        {
            image: ad300250_ai_1,
            href: "/ai-pdf-summarizer",
            isAdBlock: false,
        },
        {
            image: ad300250_ai_2,
            href: "/ai-pdf-summarizer",
            isAdBlock: false,
        },
        {
            image: ad300250_radb_1,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad300250_radb_2,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad300250_radb_3,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad300250_ap_1,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad300250_ap_2,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad300250_ap_3,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad300250_ap_4,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad300250_ap_5,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad300250_ap_6,
            href: "/exams/AP",
            isAdBlock: false,
        },
    ].filter(({ isAdBlock }) => isAdBlock === false || adBlock === isAdBlock);

/*
728x90
*/
const ad_72890_ai_1 = `${ASSETS_URL}/images/radb/72890_ai_1.png`;
const ad_72890_ai_2 = `${ASSETS_URL}/images/radb/72890_ai_2.png`;
const ad_72890_radb_1 = `${ASSETS_URL}/images/radb/72890_radb_1.png`;
const ad_72890_radb_2 = `${ASSETS_URL}/images/radb/72890_radb_2.png`;
const ad_72890_radb_3 = `${ASSETS_URL}/images/radb/72890_radb_3.png`;
const ad_72890_ap_1 = `${ASSETS_URL}/images/radb/72890_ap_1.png`;
const ad_72890_ap_2 = `${ASSETS_URL}/images/radb/72890_ap_2.png`;
const ad_72890_ap_3 = `${ASSETS_URL}/images/radb/72890_ap_3.png`;
const ad_72890_ap_4 = `${ASSETS_URL}/images/radb/72890_ap_4.png`;
const ad_72890_ap_5 = `${ASSETS_URL}/images/radb/72890_ap_5.png`;
const ad_72890_ap_6 = `${ASSETS_URL}/images/radb/72890_ap_6.png`;

const fallbacks_728x90 = (adBlock = false) =>
    [
        {
            image: ad_72890_ai_1,
            href: "/ai-pdf-summarizer",
            isAdBlock: false,
        },
        {
            image: ad_72890_ai_2,
            href: "/ai-pdf-summarizer",
            isAdBlock: false,
        },
        {
            image: ad_72890_radb_1,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad_72890_radb_2,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad_72890_radb_3,
            href: TURN_OFF_AD_BLOCK_URL,
            isAdBlock: true,
        },
        {
            image: ad_72890_ap_1,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad_72890_ap_2,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad_72890_ap_3,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad_72890_ap_4,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad_72890_ap_5,
            href: "/exams/AP",
            isAdBlock: false,
        },
        {
            image: ad_72890_ap_6,
            href: "/exams/AP",
            isAdBlock: false,
        },
    ].filter(({ isAdBlock }) => isAdBlock === false || adBlock === isAdBlock);

/*
300x600
*/
// const ad300600_ai_1 = `${ASSETS_URL}/images/radb/300600_ai_1.png`;
// const ad300600_ai_2 = `${ASSETS_URL}/images/radb/300600_ai_2.png`;
// const ad300600_radb_1 = `${ASSETS_URL}/images/radb/300600_radb_1.png`;
// const ad300600_radb_2 = `${ASSETS_URL}/images/radb/300600_radb_2.png`;
// const ad300600_radb_3 = `${ASSETS_URL}/images/radb/300600_radb_3.png`;
// const ad300600_ap_1 = `${ASSETS_URL}/images/radb/300600_ap_1.png`;
// const ad300600_ap_2 = `${ASSETS_URL}/images/radb/300600_ap_2.png`;
// const ad300600_ap_3 = `${ASSETS_URL}/images/radb/300600_ap_3.png`;
// const ad300600_ap_4 = `${ASSETS_URL}/images/radb/300600_ap_4.png`;
// const ad300600_ap_5 = `${ASSETS_URL}/images/radb/300600_ap_5.png`;
// const ad300600_ap_6 = `${ASSETS_URL}/images/radb/300600_ap_6.png`;

// const fallbacks_300x600 = (adBlock = false) =>
//     [
//         {
//             image: ad300600_ai_1,
//             href: "/ai-pdf-summarizer",
//             isAdBlock: false,
//         },
//         {
//             image: ad300600_ai_2,
//             href: "/ai-pdf-summarizer",
//             isAdBlock: false,
//         },
//         {
//             image: ad300600_radb_1,
//             href: TURN_OFF_AD_BLOCK_URL,
//             isAdBlock: true,
//         },
//         {
//             image: ad300600_radb_2,
//             href: TURN_OFF_AD_BLOCK_URL,
//             isAdBlock: true,
//         },
//         {
//             image: ad300600_radb_3,
//             href: TURN_OFF_AD_BLOCK_URL,
//             isAdBlock: true,
//         },
//         {
//             image: ad300600_ap_1,
//             href: "/exams/AP",
//             isAdBlock: false,
//         },
//         {
//             image: ad300600_ap_2,
//             href: "/exams/AP",
//             isAdBlock: false,
//         },
//         {
//             image: ad300600_ap_3,
//             href: "/exams/AP",
//             isAdBlock: false,
//         },
//         {
//             image: ad300600_ap_4,
//             href: "/exams/AP",
//             isAdBlock: false,
//         },
//         {
//             image: ad300600_ap_5,
//             href: "/exams/AP",
//             isAdBlock: false,
//         },
//         {
//             image: ad300600_ap_6,
//             href: "/exams/AP",
//             isAdBlock: false,
//         },
//     ].filter(({ isAdBlock }) => isAdBlock === false || adBlock === isAdBlock);

export const AD_SIZES_BY_ID: { [key in AdSlotId]: Record<Platform, ReplacementAds> } = {
    knowt_leaderboard_atf_mobile: {
        mobile: {
            width: 300,
            height: 50,
            fallbacks: fallbacks_320x50,
        },
        //! not shown on tablet or desktop
        tablet: {
            width: 0,
            height: 0,
            fallbacks: fallbacks_320x50,
        },
        desktop: {
            width: 0,
            height: 0,
            fallbacks: fallbacks_728x90,
        },
    },
    // Footer Ad slot, has no parent
    knowt_sticky_footer: {
        mobile: {
            width: 320,
            height: 50,
            fallbacks: fallbacks_320x50,
        },
        tablet: {
            width: 320,
            height: 100,
            fallbacks: fallbacks_320x100,
        },
        desktop: {
            width: 728,
            height: 90,
            fallbacks: fallbacks_728x90,
        },
    },

    // Sidebar ad slot
    // <!-- FREESTAR D: 300x250,160x600,300x600 T: 300x250,160x600,300x600 M: 300x250,320x50,320100 -->
    knowt_sticky_rightrail: {
        mobile: {
            width: 300,
            height: 250,
            fallbacks: fallbacks_300x250,
        },
        tablet: {
            width: 300,
            height: 250,
            fallbacks: fallbacks_300x250,
        },
        desktop: {
            width: 300,
            height: 250,
            fallbacks: fallbacks_300x250,
        },
    },

    // Sidebar square ad slot
    // <!-- FREESTAR D: 300x250
    knowt_rightrail_top: {
        mobile: {
            width: 300,
            height: 250,
            fallbacks: fallbacks_300x250,
        },
        tablet: {
            width: 300,
            height: 250,
            fallbacks: fallbacks_300x250,
        },
        desktop: {
            width: 300,
            height: 250,
            fallbacks: fallbacks_300x250,
        },
    },

    // <!-- FREESTAR Repeatable leaderboard ad - to be placed between sections D: 728x90 T: 728x90 M: 320x50,320100  START -->
    knowt_dynamic_incontent: {
        mobile: {
            width: 320,
            height: 100,
            fallbacks: fallbacks_320x100,
        },
        tablet: {
            width: 320,
            height: 100,
            fallbacks: fallbacks_320x100,
        },
        desktop: {
            width: 728,
            height: 90,
            fallbacks: fallbacks_728x90,
        },
    },

    // <!-- FREESTAR Video Ad Slot. Sizes are scaled versions of 640x480
    knowt_primis: {
        mobile: {
            width: 320,
            height: 100,
            fallbacks: fallbacks_320x100,
        },
        tablet: {
            width: 300,
            height: 180,
            fallbacks: fallbacks_300x180,
        },
        desktop: {
            width: 300,
            height: 180,
            fallbacks: fallbacks_300x180,
        },
    },
};

const GenericAdSlot = memo(
    ({
        placement,
        slotId,
        classes = [],
        style,
    }: {
        placement: AdSlotId;
        slotId?: number;
        classes?: string[];
        style?: React.CSSProperties;
    }) => {
        const { smDown, lgDown } = useBreakPoints();
        const { user, isSubscriptionActive, isUnder13, canShowAds } = useCurrentUser();

        const deviceType = smDown ? "mobile" : lgDown ? "tablet" : "desktop";
        const { width, height, fallbacks: _fallbacks } = AD_SIZES_BY_ID[placement][deviceType];

        if (!user || isSubscriptionActive || isUnder13 || !canShowAds) {
            return null;
        }

        if (!width || !height) {
            // ad is not enabled for these device dimensions
            return null;
        }

        // const targeting = { key1: "value1", key2: "value2" };

        const adDiv = (
            <FreestarAdSlot
                publisher={FREESTAR_PUBLISHER}
                placementName={placement}
                // unique ID
                slotId={slotId ? `${placement}_${slotId}` : placement}
                // targeting={targeting}
                channel="custom_channel"
                classList={classes}
                onNewAdSlotsHook={placementName => {
                    // biome-ignore lint/suspicious/noConsole:
                    console.log("creating ad", placementName);
                }}
                onDeleteAdSlotsHook={placementName => {
                    // biome-ignore lint/suspicious/noConsole:
                    console.log("destroying ad", placementName);
                }}
                onAdRefreshHook={placementName => {
                    // biome-ignore lint/suspicious/noConsole:
                    console.log("refreshing ad", placementName);
                }}
            />
        );

        if (!width || !height) {
            return adDiv;
        }

        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "transparent",
                    width: width + 20,
                    height: height + 20,
                    ...style,
                }}
                data-testid="auto-id-e86ba2c2-dd60-43ba-ac73-735fa5645fb1">
                {adDiv}
            </div>
        );
    },
    (prevProps, nextProps) => isEqual(prevProps, nextProps)
);

GenericAdSlot.displayName = "GenericAdSlot";

const AdSlotFiller = ({ height }: { height?: number | string }) => {
    if (!height) return null;

    return (
        <div
            style={{ height }}
            data-testid="auto-id-be1fa2a3-9e37-48f3-bda8-bbd2f63b8dfb" />
    );
};

export const HorizontallyCenteredRepeatableLeaderboardAdSlot = ({
    className,
    containerStyle = {},
    slotId = undefined,
    isEnabled = true,
    reserveHeight,
}: {
    className?: string;
    containerStyle?: React.CSSProperties;
    slotId?: number;
    isEnabled?: boolean;
    reserveHeight?: number | string;
} = {}) => {
    const { isSubscriptionActive, isUnder13 } = useCurrentUser();

    if (isSubscriptionActive || isUnder13) return <AdSlotFiller height={reserveHeight} />;

    if (!isEnabled) return null;

    return (
        <div
            className={className}
            style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                ...containerStyle,
            }}
            data-testid="auto-id-49dfbc9d-aee9-4527-a877-2786a00b5b44">
            {/*// <!-- FREESTAR Repeatable leaderboard ad - to be placed between sections D: 728x90 T: 728x90 M: 320x50,320100  START -->*/}
            <GenericAdSlot placement={"knowt_dynamic_incontent"} slotId={slotId} />
        </div>
    );
};

export const SidebarAdSlot = ({
    style,
    single = false,
    showButton = true,
}: {
    style?: React.CSSProperties;
    single?: boolean;
    showButton?: boolean;
}) => {
    const { isSubscriptionActive, isUnder13, canShowAds } = useCurrentUser();
    // <!-- FREESTAR D: 300x250,160x600,300x600 T: 300x250,160x600,300x600 M: 300x250,320x50,320100 -->

    if (isSubscriptionActive || isUnder13 || !canShowAds) return null;

    return (
        <FlexColumnAlignJustifyCenter>
            <GenericAdSlot placement={"knowt_rightrail_top"} style={style} />
            {!single && <GenericAdSlot placement={"knowt_sticky_rightrail"} style={style} />}
            {showButton && <RemoveAds />}
        </FlexColumnAlignJustifyCenter>
    );
};

export const LeaderboardATFMobile = () => {
    return (
        <div
            style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
            }}
            data-testid="auto-id-6926cf83-f624-4cec-9c31-1b4f9747a742">
            <GenericAdSlot placement={"knowt_leaderboard_atf_mobile"} />
        </div>
    );
};

export default GenericAdSlot;
